import { SolSeverity, SolSeverityFavor } from '../models/core/sol-severity';
import { SolColor } from '../models/core/sol-color';

export class SeverityTools {
  public static calculateBlocks(score: SolSeverity | undefined): string[] {
    const blockCount = SeverityTools.calculateBlockCount(score);
    const filledBlocks = SeverityTools.calculateFilledBlocks(score, blockCount);
    const color = SeverityTools.calculateBlockColor(score, filledBlocks);

    return Array.from({ length: blockCount }, (_, index) => (index < filledBlocks ? color : 'opacity-mid'));
  }

  public static calculateColor(score: SolSeverity): SolColor {
    const blockCount = SeverityTools.calculateBlockCount(score);
    const filledBlocks = SeverityTools.calculateFilledBlocks(score, blockCount);
    return SeverityTools.calculateBlockColor(score, filledBlocks);
  }

  private static calculateBlockCount(score: SolSeverity | undefined): number {
    return score?.format === 'grade' ? 6 : 4;
  }

  private static calculateFilledBlocks(score: SolSeverity | undefined, blockCount: number): number {
    if (score?.value === undefined || blockCount === 0 || score.maxValue === 0) {
      return 0;
    }

    if (score.format === 'grade') {
      return SeverityTools.calculateGradeIndex(score, blockCount) + 1;
    }

    const ratio = Math.max(0, Math.min(1, score.value / score.maxValue));
    return Math.min(blockCount, Math.ceil(ratio * blockCount));
  }

  private static calculateBlockColor(score: SolSeverity | undefined, filledBlocks: number): SolColor {
    if (score === undefined || filledBlocks === 0) {
      return 'opacity-mid';
    }

    if (score.neutral) {
      return 'opacity-high';
    }

    if (score.format === 'grade') {
      return SeverityTools.gradeIndexToColor(filledBlocks - 1, score.favor);
    }

    return SeverityTools.severityToColor(filledBlocks, score.favor);
  }

  private static calculateGradeIndex(score: SolSeverity, blockCount: number): number {
    const ratio = Math.max(0, Math.min(1, (score.value ?? 0) / (score.maxValue || 1)));
    return Math.min(blockCount - 1, Math.floor(ratio * blockCount));
  }

  private static severityToColor(filledBlocks: number, favor: SolSeverityFavor): SolColor {
    if (filledBlocks === 0) {
      return 'opacity-mid';
    }
    const colors: SolColor[] =
      favor === 'low' ? ['green', 'yellow', 'orange', 'red'] : ['red', 'orange', 'yellow', 'green'];
    const index = Math.min(Math.max(filledBlocks - 1, 0), colors.length - 1);
    return colors[index];
  }

  private static gradeIndexToColor(gradeIndex: number, favor: SolSeverityFavor): SolColor {
    const colors: SolColor[] =
      favor === 'low'
        ? ['green', 'yellow', 'yellow', 'orange', 'orange', 'red']
        : ['red', 'orange', 'orange', 'yellow', 'yellow', 'green'];
    const index = Math.min(Math.max(gradeIndex, 0), colors.length - 1);
    return colors[index];
  }
}
